<script setup lang="ts">
/** CDD Legal Person (TMP004) 문서 본문. 체크박스는 if-wrap bt_* 로 저장. */
import { inject, computed } from 'vue';
import { CDD_DOC_CTX } from './cddContext';
import { CDD_DOCS, CDD_SHAREHOLDER_MAX } from './cddFields';
import CddHeader from './CddHeader.vue';
import CddPersonTransacting from './CddPersonTransacting.vue';
import CddDeclaration from './CddDeclaration.vue';
import CddVerification from './CddVerification.vue';
import CddDocVarField from './CddDocVarField.vue';

const ctx = inject(CDD_DOC_CTX)!;
const BIZ = CDD_DOCS.legal_person.checkboxGroup!.options;
// 동적 행 수 (자동 채워진 주주/BO 데이터만큼; 기본 2)
const shRowN = computed(() => ctx.shareholderRowCount.value);
const boRowN = computed(() => ctx.boRowCount.value);
const canAddSh = computed(() => shRowN.value < CDD_SHAREHOLDER_MAX);
const canAddBo = computed(() => boRowN.value < CDD_SHAREHOLDER_MAX);
</script>

<template>
  <div>
    <CddHeader />
    <table class="cdd-tbl" style="margin-top:14px"><tbody>
      <tr><td class="cdd-sec" colspan="2">2) For LEGAL PERSON <span class="cdd-cn">关于法人</span></td></tr>
      <tr><td class="cdd-lcell">Company/Business Name <span class="cdd-cn">公司/企业名称</span></td><td><CddDocVarField field-key="lp_company_name" /></td></tr>
      <tr><td class="cdd-lcell">Business Registration No. <span class="cdd-cn">营业注册号</span></td><td><CddDocVarField field-key="lp_registration_no" /></td></tr>
      <tr>
        <td class="cdd-lcell">Business Type <span class="cdd-cn">商业类型</span></td>
        <td>
          <span v-for="b in BIZ" :key="b.value" class="cdd-cbx" @click="ctx.setCheckbox(b.value)">
            <v-icon size="15" class="box" :color="ctx.checkboxValue.value === b.value ? 'primary' : ''">{{ ctx.checkboxValue.value === b.value ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>{{ b.label }}<template v-if="b.value === 'bt_other'"> <span class="cdd-cn">其他</span>: <CddDocVarField field-key="lp_other_type" /></template>
          </span>
        </td>
      </tr>
      <tr><td class="cdd-lcell">Country of Incorporation/Registration <span class="cdd-cn">注册国家</span></td><td><CddDocVarField field-key="lp_country_of_incorporation" /></td></tr>
      <tr><td class="cdd-lcell">Registered Office Address <span class="cdd-cn">注册办公地址</span></td><td><CddDocVarField field-key="lp_registered_address" /></td></tr>
      <tr><td class="cdd-lcell">Town <span class="cdd-cn">城镇</span></td><td><CddDocVarField field-key="lp_town" /></td></tr>
      <tr><td class="cdd-lcell">State <span class="cdd-cn">州际</span></td><td><CddDocVarField field-key="lp_state" /></td></tr>
      <tr><td class="cdd-lcell">Postcode <span class="cdd-cn">邮政编码</span> &nbsp; <CddDocVarField field-key="lp_postcode" /></td><td>Country <span class="cdd-cn">乡村/国家</span> &nbsp; <CddDocVarField field-key="lp_country" /></td></tr>
      <tr><td class="cdd-lcell">Principal Place of Business (if different)</td><td><CddDocVarField field-key="lp_business_address" /></td></tr>
      <tr><td class="cdd-lcell">Town <span class="cdd-cn">城镇</span></td><td><CddDocVarField field-key="lp_biz_town" /></td></tr>
      <tr><td class="cdd-lcell">State <span class="cdd-cn">州际</span></td><td><CddDocVarField field-key="lp_biz_state" /></td></tr>
      <tr><td class="cdd-lcell">Postcode <span class="cdd-cn">邮政编码</span> &nbsp; <CddDocVarField field-key="lp_biz_postcode" /></td><td>Country <span class="cdd-cn">乡村/国家</span> &nbsp; <CddDocVarField field-key="lp_biz_country" /></td></tr>
      <tr><td class="cdd-lcell">Principle Business <span class="cdd-cn">主要业务</span></td><td><CddDocVarField field-key="lp_principle_business" /></td></tr>
      <tr><td class="cdd-lcell">Contact No. <span class="cdd-cn">联系号码</span></td><td><CddDocVarField field-key="lp_contact_no" /></td></tr>
      <tr><td class="cdd-lcell">Purpose of Transaction <span class="cdd-cn">交易目的</span></td><td><CddDocVarField field-key="lp_purpose_of_txn" /></td></tr>
      <tr><td class="cdd-lcell">Directors/Partners <span class="cdd-cn">董事/合伙人姓名</span></td><td><CddDocVarField field-key="lp_directors_partners" /></td></tr>
    </tbody></table>

    <table class="cdd-tbl" style="margin-top:14px"><tbody>
      <tr>
        <th style="width:34%">
          Shareholder(s)/Beneficial Owner(s) <span class="cdd-cn">股东/实益所有人</span>
        </th>
        <th>Name <span class="cdd-cn">名称</span></th>
        <th>Types of shares <span class="cdd-cn">股份类型</span></th>
        <th>
          Percentage <span class="cdd-cn">百分比</span>
          <span class="cdd-row-acts">
            <button type="button" class="cdd-row-btn" title="Add row" :disabled="!canAddSh" @click="ctx.addShareholderRow()">
              <v-icon size="14">mdi-plus</v-icon>
            </button>
          </span>
        </th>
      </tr>
      <tr v-for="n in shRowN" :key="'sh-' + n">
        <td class="cdd-bo-idx">
          <button
            type="button"
            class="cdd-row-btn cdd-row-btn--del"
            title="Remove row"
            @click="ctx.removeShareholderRow(n)"
          >
            <v-icon size="14">mdi-trash-can-outline</v-icon>
          </button>
        </td>
        <td><CddDocVarField :field-key="`shareholder_name_${n}`" /></td>
        <td><CddDocVarField :field-key="`share_type_${n}`" /></td>
        <td><CddDocVarField :field-key="`share_pct_${n}`" /></td>
      </tr>
    </tbody></table>

    <table class="cdd-tbl" style="margin-top:14px"><tbody>
      <tr>
        <th style="width:40%">
          Beneficial Owners through other means <span class="cdd-cn">通过其他方式获得实益的所有人</span>
        </th>
        <th>Name <span class="cdd-cn">名称</span></th>
        <th>
          Type of ownership/control <span class="cdd-cn">所有权/控制权/关系</span>
          <span class="cdd-row-acts">
            <button type="button" class="cdd-row-btn" title="Add row" :disabled="!canAddBo" @click="ctx.addBoRow()">
              <v-icon size="14">mdi-plus</v-icon>
            </button>
          </span>
        </th>
      </tr>
      <tr v-for="n in boRowN" :key="n">
        <td class="cdd-bo-idx">
          <button
            type="button"
            class="cdd-row-btn cdd-row-btn--del"
            title="Remove row"
            @click="ctx.removeBoRow(n)"
          >
            <v-icon size="14">mdi-trash-can-outline</v-icon>
          </button>
        </td>
        <td><CddDocVarField :field-key="`bo_name_${n}`" /></td>
        <td><CddDocVarField :field-key="`bo_relationship_${n}`" /></td>
      </tr>
    </tbody></table>

    <table class="cdd-tbl" style="margin-top:14px"><tbody><tr><td class="cdd-lcell">Senior Management <span class="cdd-cn">高级管理人员</span></td><td><CddDocVarField field-key="lp_senior_management" /></td></tr></tbody></table>

    <CddPersonTransacting />
    <CddDeclaration />
    <CddVerification />
  </div>
</template>

<style scoped>
.cdd-row-acts { display: inline-flex; margin-left: 8px; vertical-align: middle; }
.cdd-bo-idx { width: 36px; text-align: center; vertical-align: middle !important; }
.cdd-row-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 6px; border: 1px solid #c5d0e0;
  background: #f7f9fc; color: #1B84FF; cursor: pointer; padding: 0; vertical-align: middle;
}
.cdd-row-btn:hover:not(:disabled) { background: #eaf3ff; border-color: #1B84FF; }
.cdd-row-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.cdd-row-btn--del { color: #9aa3b2; border-color: #dde3ec; background: #fff; }
.cdd-row-btn--del:hover { color: #c62828; border-color: #ef9a9a; background: #fff5f5; }
</style>
